/**
 * Direct Messages Mutation Hooks (FRONT-308)
 *
 * The paid-send hook owns the idempotency lifecycle: callers obtain a
 * client_id via getOrCreateSendClientId inside the tap handler; this hook
 * clears it on success (including `deduplicated: true`) and leaves it in
 * place on failure so a retry reuses the same UUID. No toasts here — surfaces
 * own the failure UX (422 fields via getDmErrorField).
 */

import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  DmMessage,
  DmMessagesPage,
  DmSentMessage,
  DmStudioThreadsPage,
  DmThreadsPage,
  Wallet,
} from '@/types';
import { dmClient } from '../client/dm.client';
import { clearSendClientId } from '../lib/dm-client-id';
import { queryKeys } from '../query-keys';

type MessagesKey = ReturnType<typeof queryKeys.dm.messages>;

function messagesKey(threadUuid: string): MessagesKey {
  return queryKeys.dm.messages(threadUuid);
}

/** Optimistically append a pending message; returns the snapshot for rollback. */
function appendPendingMessage(
  queryClient: QueryClient,
  threadUuid: string,
  message: DmMessage
): DmMessagesPage | undefined {
  const previous = queryClient.getQueryData<DmMessagesPage>(messagesKey(threadUuid));
  if (!previous) return undefined;

  queryClient.setQueryData<DmMessagesPage>(messagesKey(threadUuid), {
    ...previous,
    messages: [...previous.messages, message],
  });
  return previous;
}

/**
 * Swap the pending entry for the confirmed one. On `deduplicated: true` the
 * confirmed message may already be in the list (a poll landed it) — never
 * append it twice.
 */
function resolvePendingMessage(
  queryClient: QueryClient,
  threadUuid: string,
  pendingUuid: string,
  confirmed: DmMessage
): void {
  queryClient.setQueryData<DmMessagesPage>(messagesKey(threadUuid), (previous) => {
    if (!previous) return previous;
    const messages = previous.messages.filter((m) => m.uuid !== pendingUuid);
    if (messages.some((m) => m.uuid === confirmed.uuid)) return { ...previous, messages };
    return { ...previous, messages: [...messages, confirmed] };
  });
}

function rollbackMessages(
  queryClient: QueryClient,
  threadUuid: string | undefined,
  previous?: DmMessagesPage
): void {
  if (threadUuid && previous) {
    queryClient.setQueryData(messagesKey(threadUuid), previous);
  }
}

function toDmMessage(sent: DmSentMessage, senderType: DmMessage['sender_type']): DmMessage {
  return {
    uuid: sent.uuid,
    sender_type: senderType,
    body: sent.body,
    coins_charged: sent.coins_charged,
    read_at: null,
    created_at: sent.created_at,
  };
}

export interface SendDmMessageInput {
  channelUuid: string;
  /** Existing thread when known. First sends have none — no optimistic append; the thread arrives on success. */
  threadUuid?: string;
  body: string;
  /** From getOrCreateSendClientId, called in the tap handler. */
  clientId: string;
}

export function useSendDmMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ channelUuid, body, clientId }: SendDmMessageInput) =>
      dmClient.sendMessage(channelUuid, { body, client_id: clientId }),

    onMutate: async ({ threadUuid, body, clientId }) => {
      if (!threadUuid) return { previous: undefined };
      await queryClient.cancelQueries({ queryKey: messagesKey(threadUuid) });
      const previous = appendPendingMessage(queryClient, threadUuid, {
        uuid: `pending-${clientId}`,
        sender_type: 'user',
        body,
        // Real price lands with the confirmed message; pending bubbles hide the chip.
        coins_charged: 0,
        read_at: null,
        created_at: new Date().toISOString(),
      });
      return { previous };
    },

    onSuccess: (result, { channelUuid, threadUuid, clientId }) => {
      clearSendClientId(channelUuid);
      queryClient.setQueryData<Wallet>(queryKeys.coins.wallet(), (wallet) =>
        wallet ? { ...wallet, balance: result.balance_after } : wallet
      );
      if (threadUuid) {
        resolvePendingMessage(
          queryClient,
          threadUuid,
          `pending-${clientId}`,
          toDmMessage(result.message, 'user')
        );
      }
    },

    // client_id intentionally NOT cleared — a retry must reuse the same UUID.
    onError: (_error, { threadUuid }, context) => {
      rollbackMessages(queryClient, threadUuid, context?.previous);
    },

    onSettled: (result, _error, { threadUuid }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dm.threads() });
      const settledThread = threadUuid ?? result?.message.thread_uuid;
      if (settledThread) {
        queryClient.invalidateQueries({ queryKey: messagesKey(settledThread) });
      }
    },
  });
}

/** Generic unread-count zeroing for both inbox shapes. */
function zeroThreadUnread<T extends DmThreadsPage | DmStudioThreadsPage>(
  queryClient: QueryClient,
  key: readonly unknown[],
  threadUuid: string
): T | undefined {
  const previous = queryClient.getQueryData<T>(key);
  if (!previous) return undefined;

  queryClient.setQueryData<T>(key, {
    ...previous,
    threads: previous.threads.map((t) => (t.uuid === threadUuid ? { ...t, unread_count: 0 } : t)),
  });
  return previous;
}

export function useMarkDmThreadRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (threadUuid: string) => dmClient.markRead(threadUuid),
    onMutate: async (threadUuid) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.dm.threads() });
      const previous = zeroThreadUnread<DmThreadsPage>(
        queryClient,
        queryKeys.dm.threads(),
        threadUuid
      );
      return { previous };
    },
    onError: (_error, _threadUuid, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKeys.dm.threads(), context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dm.threads() });
    },
  });
}

export interface SendStudioReplyInput {
  threadUuid: string;
  body: string;
}

export function useSendStudioReply() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ threadUuid, body }: SendStudioReplyInput) =>
      dmClient.sendStudioReply(threadUuid, body),

    onMutate: async ({ threadUuid, body }) => {
      await queryClient.cancelQueries({ queryKey: messagesKey(threadUuid) });
      const pendingUuid = `pending-reply-${Date.now()}`;
      const previous = appendPendingMessage(queryClient, threadUuid, {
        uuid: pendingUuid,
        sender_type: 'creator',
        body,
        coins_charged: 0,
        read_at: null,
        created_at: new Date().toISOString(),
      });
      return { previous, pendingUuid };
    },

    onSuccess: (message, { threadUuid }, context) => {
      resolvePendingMessage(
        queryClient,
        threadUuid,
        context.pendingUuid,
        toDmMessage(message, 'creator')
      );
    },

    onError: (_error, { threadUuid }, context) => {
      rollbackMessages(queryClient, threadUuid, context?.previous);
    },

    onSettled: (_message, _error, { threadUuid }) => {
      queryClient.invalidateQueries({ queryKey: messagesKey(threadUuid) });
      queryClient.invalidateQueries({ queryKey: queryKeys.dm.studioThreads() });
    },
  });
}

export function useMarkStudioThreadRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (threadUuid: string) => dmClient.markStudioRead(threadUuid),
    onMutate: async (threadUuid) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.dm.studioThreads() });
      const previous = zeroThreadUnread<DmStudioThreadsPage>(
        queryClient,
        queryKeys.dm.studioThreads(),
        threadUuid
      );
      return { previous };
    },
    onError: (_error, _threadUuid, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKeys.dm.studioThreads(), context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dm.studioThreads() });
    },
  });
}
